import { useEffect, useState } from "react";
import { useUserDataContext } from "../../contexts/UserDataContext";
import "../../styles/legacy-css/SelectLocation.css";
import {
  StyledSLContainer,
  StyledSLDash,
  StyledSLDownArrow,
  StyledSLClearSearchButton,
  StyledSLCurrentLocationLabel,
  StyledSLNavy,
  StyledSLNoResults,
  StyledSLBreadcrumb,
  StyledSLSearchInput,
  StyledSLSearchWrapper,
  StyledSLSelected,
  StyledSLSelectedLabel,
  StyledSLSelectionContainer,
  StyledSLText,
  StyledSLUl,
  StyledSLUpArrow,
} from "../../styles/components/SelectLocation.styled";
import {
  StyledOuterPopup,
  StyledInnerPopup,
  StyledPopupButton,
} from "../../styles/components/Popup.styled";
import { UNTITLED } from "../../utils/Constants";
import { useScrollLock, useDebounce } from "../../hooks";

const SEARCH_DEBOUNCE_MS = 500;

export function SelectLocationPopup() {
  // Lock scrolling when modal is open
  useScrollLock(true);

  return (
    <StyledOuterPopup>
      <StyledInnerPopup>
        <SelectLocation />
      </StyledInnerPopup>
    </StyledOuterPopup>
  );
}

function SelectLocation() {
  const {
    parentMap,
    itemToMove,
    setItemToMove,
    itemMap,
    moveItem,
    setSearchActive,
    getItemLocation,
    getItemChildren,
    multiSelectMode,
    selectedItemsToMove,
    setMultiSelectMode,
    setSelectedItemsToMove,
  } = useUserDataContext();
  const [selection, setSelection] = useState("MASTER");
  const [showChildrenArr, setShowChildrenArr] = useState<string[]>(["MASTER"]);
  const [parentList, setParentList] = useState<string[]>(["MASTER"]);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);
  // Whether to show search results vs. the tree switches instantly on the raw
  // query so clearing/selecting doesn't wait on the debounce
  const isSearching = searchQuery.trim().length > 0;
  const trimmedDebouncedQuery = debouncedSearchQuery.trim().toLowerCase();

  // Determine which items are being moved
  const itemsBeingMoved = multiSelectMode ? selectedItemsToMove : [itemToMove];

  // The tree view hides a moved item's entire subtree by suppressing it at the
  // root (renderChildren never expands into itemsBeingMoved), which cascades
  // to every descendant automatically. Search has no such recursion — it's a
  // flat scan over itemMap — so descendants must be excluded explicitly here
  // to avoid offering a location that's actually inside the item being moved.
  const excludedFromSelection = new Set(itemsBeingMoved);
  itemsBeingMoved.forEach((id) => {
    getItemChildren(id).forEach((descendantId) => excludedFromSelection.add(descendantId));
  });

  // The immediate parent (current containing location) of the item(s) being
  // moved - the second-to-last entry in parentList, right before the item itself
  const directParentId = parentList[parentList.length - 2];

  // Items whose name contains the debounced search query
  const searchResults = trimmedDebouncedQuery
    ? Object.values(itemMap).filter(
        (item) =>
          item.ItemId &&
          !excludedFromSelection.has(item.ItemId) &&
          (item.Name ?? "").toLowerCase().includes(trimmedDebouncedQuery)
      )
    : [];

  // Shows the item's parent and grandparent (e.g. "Pantry / Shelf 2") so
  // identically-named locations (multiple "Box 1"s) can be told apart in
  // search results without showing the full ancestor path
  function getBreadcrumb(itemId: string) {
    const ancestors = getItemLocation(itemId).slice(0, -1);
    return ancestors
      .slice(-2)
      .map((id) => (id === "MASTER" ? "MASTER" : itemMap[id]?.Name || UNTITLED))
      .join(" / ");
  }

  function updateShowChildrenArr(itemId: string) {
    if (parentMap[itemId] != null) {
      if (showChildrenArr.includes(itemId)) {
        setShowChildrenArr((showChildrenArr) => {
          return showChildrenArr.filter((id) => id !== itemId);
        });
      } else {
        setShowChildrenArr([...showChildrenArr, itemId]);
      }
    }
  }

  function renderChildren(itemId: string) {
    return (
      <>
        {!itemsBeingMoved.includes(itemId) && (
          <ul>
            {Array.from(parentMap[itemId])
              .filter((id) => !itemsBeingMoved.includes(id))
              .map((s) => {
                return (
                  <li key={itemMap[s].ItemId}>
                    {parentMap[s] == null && (
                      <span
                        onClick={() =>
                          updateShowChildrenArr(itemMap[s].ItemId!)
                        }
                      >
                        <StyledSLDash />
                      </span>
                    )}
                    {showChildrenArr.includes(s) && (
                      <span
                        className="expandable"
                        onClick={() =>
                          updateShowChildrenArr(itemMap[s].ItemId!)
                        }
                      >
                        <StyledSLUpArrow />
                      </span>
                    )}
                    {parentMap[s] != null && !showChildrenArr.includes(s) && (
                      <span
                        className="expandable"
                        onClick={() =>
                          updateShowChildrenArr(itemMap[s].ItemId!)
                        }
                      >
                        <StyledSLDownArrow />
                      </span>
                    )}
                    {/* Display the name of the items*/}
                    <span onClick={() => handleSelect(itemMap[s].ItemId!)}>
                      {selection != "MASTER" &&
                      //Bold the selected item
                      itemMap[s].ItemId == itemMap[selection].ItemId ? (
                        itemMap[s].Name == null || itemMap[s].Name == "" ? (
                          <strong>
                            <StyledSLSelected>{UNTITLED}</StyledSLSelected>
                          </strong>
                        ) : (
                          <strong>
                            <StyledSLSelected>{itemMap[s].Name}</StyledSLSelected>
                          </strong>
                        )
                      ) : //Change the color of the text if the name is not selected but in the parent hierarchy for the item
                      parentList.includes(itemMap[s].ItemId) ? (
                        itemMap[s].Name == null || itemMap[s].Name == "" ? (
                          //Bold the item's current (direct parent) location
                          itemMap[s].ItemId === directParentId ? (
                            <strong>
                              <StyledSLNavy>{UNTITLED}</StyledSLNavy>
                            </strong>
                          ) : (
                            <StyledSLNavy>{UNTITLED}</StyledSLNavy>
                          )
                        ) : itemMap[s].ItemId === directParentId ? (
                          <strong>
                            <StyledSLNavy>{itemMap[s].Name}</StyledSLNavy>
                          </strong>
                        ) : (
                          <StyledSLNavy>{itemMap[s].Name}</StyledSLNavy>
                        )
                      ) : itemMap[s].Name == null || itemMap[s].Name == "" ? (
                        UNTITLED
                      ) : (
                        itemMap[s].Name
                      )}
                    </span>
                    {selection != "MASTER" &&
                      itemMap[s].ItemId == itemMap[selection].ItemId && (
                        <StyledSLSelectedLabel>Selected</StyledSLSelectedLabel>
                      )}
                    {itemMap[s].ItemId === directParentId && (
                      <StyledSLCurrentLocationLabel>
                        Current Location
                      </StyledSLCurrentLocationLabel>
                    )}
                    {showChildrenArr.includes(s) && renderChildren(s)}
                  </li>
                );
              })}
          </ul>
        )}
      </>
    );
  }

  async function move() {
    // Check the destination isn't one of the items being moved, or one of
    // their own descendants (which would be a circular move)
    if (excludedFromSelection.has(selection)) {
      alert("Cannot move item(s) into themselves or their own contents");
      return;
    }

    // Move all selected items
    if (multiSelectMode) {
      // Move all items in the selection
      for (const itemId of selectedItemsToMove) {
        await moveItem(itemId, selection);
      }
      // Clear multi-select mode
      setMultiSelectMode(false);
      setSelectedItemsToMove([]);
    } else {
      // Single item move
      await moveItem(itemToMove, selection);
    }

    // Close the modal
    setItemToMove("");
  }

  useEffect(() => {
    // Get parent list for the first item being moved
    const firstItemId = multiSelectMode ? selectedItemsToMove[0] : itemToMove;
    if (firstItemId) {
      setParentList(getItemLocation(firstItemId));
    }
  }, []);

  // Select a location, clear any active search, and expand the tree to just
  // that item's ancestors (collapsing every other branch, including the
  // selection's own children)
  function handleSelect(itemId: string) {
    setSelection(itemId);
    setSearchQuery("");
    setShowChildrenArr(
      itemId === "MASTER" ? [] : getItemLocation(itemId).slice(1, -1)
    );
  }

  // Close handler
  function handleClose() {
    if (multiSelectMode) {
      setMultiSelectMode(false);
      setSelectedItemsToMove([]);
    }
    setItemToMove("");
  }

  return (
    <StyledSLContainer>
      <StyledSLText>
        {multiSelectMode
          ? `Currently Moving ${selectedItemsToMove.length} Item${selectedItemsToMove.length !== 1 ? 's' : ''}`
          : `Currently Moving "${itemMap[itemToMove].Name}"`}
      </StyledSLText>
      <StyledSLSearchWrapper>
        <StyledSLSearchInput
          type="text"
          placeholder="Search locations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <StyledSLClearSearchButton
            type="button"
            onClick={() => setSearchQuery("")}
          >
            Clear
          </StyledSLClearSearchButton>
        )}
      </StyledSLSearchWrapper>

      <StyledSLSelectionContainer>
        {isSearching ? (
          <StyledSLUl>
            {searchResults.length === 0 ? (
              <StyledSLNoResults>No locations found</StyledSLNoResults>
            ) : (
              searchResults.map((item) => (
                <li key={item.ItemId}>
                  <span onClick={() => handleSelect(item.ItemId!)}>
                    {getBreadcrumb(item.ItemId!) && (
                      <StyledSLBreadcrumb>
                        {getBreadcrumb(item.ItemId!)} /{" "}
                      </StyledSLBreadcrumb>
                    )}
                    {selection === item.ItemId ? (
                      <strong>
                        <StyledSLSelected>{item.Name || UNTITLED}</StyledSLSelected>
                      </strong>
                    ) : item.ItemId === directParentId ? (
                      <strong>
                        <StyledSLNavy>{item.Name || UNTITLED}</StyledSLNavy>
                      </strong>
                    ) : (
                      <strong>{item.Name || UNTITLED}</strong>
                    )}
                  </span>
                  {selection === item.ItemId && (
                    <StyledSLSelectedLabel>Selected</StyledSLSelectedLabel>
                  )}
                  {item.ItemId === directParentId && (
                    <StyledSLCurrentLocationLabel>
                      Current Location
                    </StyledSLCurrentLocationLabel>
                  )}
                </li>
              ))
            )}
          </StyledSLUl>
        ) : (
          <StyledSLUl>
            <li onClick={() => handleSelect("MASTER")}>MASTER</li>
            {renderChildren("MASTER")}
          </StyledSLUl>
        )}
      </StyledSLSelectionContainer>

      <div className="move-item-description">
        {multiSelectMode ? (
          <>
            Move {selectedItemsToMove.length} item{selectedItemsToMove.length !== 1 ? 's' : ''} to "
            {itemMap[selection] == null
              ? "MASTER"
              : itemMap[selection].Name == null
              ? UNTITLED
              : itemMap[selection].Name}
            "?
          </>
        ) : (
          <>
            Move "{itemMap[itemToMove].Name}" to "
            {itemMap[selection] == null
              ? "MASTER"
              : itemMap[selection].Name == null
              ? UNTITLED
              : itemMap[selection].Name}
            "?
          </>
        )}
      </div>
      <StyledPopupButton onClick={move}>Confirm Move</StyledPopupButton>
      <StyledPopupButton onClick={handleClose}>
        {" "}
        Close
      </StyledPopupButton>
    </StyledSLContainer>
  );
}

export default SelectLocation;
